<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { graphqlQuery, AUTHENTICATE_APPLE_ID_MUTATION, type AuthenticateAppleIdResponse } from '../../graphql';
import { setAuthCookies, redirectToDashboard } from '../../services/authCookies';
import { errorToast, successToast } from '../../services/toasts';

const appleClientId = computed(() => window.__APPLE_CLIENT_ID__ || '');
const appleRedirectUri = computed(() => window.__APPLE_REDIRECT_URI__ || '');
const oauthClientId = computed(() => window.__OAUTH_CLIENT_ID__ || '');
const isEnabled = computed(() => !!appleClientId.value && !!oauthClientId.value);
const isInitialized = ref(false);

async function authenticate(code: string) {
  try {
    const response = await graphqlQuery<AuthenticateAppleIdResponse>(AUTHENTICATE_APPLE_ID_MUTATION, {
      authorizationCode: code,
      clientId: oauthClientId.value,
    });

    const accessToken = response.authenticateAppleId?.accessToken;
    const errors = response.authenticateAppleId?.errors;

    if (accessToken?.token) {
      setAuthCookies(accessToken);
      successToast('Successfully authenticated');
      redirectToDashboard();
      return;
    }

    if (errors) {
      errorToast(`Unable to authenticate: ${errors.message}`);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'An error occurred';
    errorToast(message);
  }
}

async function signIn() {
  if (!window.AppleID?.auth) {
    errorToast('Apple Sign-In is not available');
    return;
  }

  try {
    const response = await window.AppleID.auth.signIn();
    if (response.authorization.code) {
      await authenticate(response.authorization.code);
    }
  } catch (error) {
    console.error('Apple Sign-In Error:', error);
  }
}

onMounted(() => {
  if (!isEnabled.value) return;

  const checkAppleLoaded = setInterval(() => {
    if (window.AppleID?.auth) {
      clearInterval(checkAppleLoaded);

      window.AppleID.auth.init({
        clientId: appleClientId.value,
        scope: 'name email',
        redirectURI: appleRedirectUri.value,
        usePopup: true,
      });

      isInitialized.value = true;
    }
  }, 100);

  setTimeout(() => clearInterval(checkAppleLoaded), 5000);
});
</script>

<template>
  <button
    v-if="isEnabled"
    type="button"
    :disabled="!isInitialized"
    class="w-sso-button h-sso-button flex items-center rounded-[3px] border border-[#d1d1d1] bg-white text-sm shadow-sm cursor-pointer transition-shadow duration-200 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
    @click="signIn"
  >
    <svg
      class="w-[18px] h-auto ml-[13px]"
      width="21"
      height="25"
      viewBox="0 0 21 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.0808 19.2033C19.7218 20.0418 19.2967 20.8136 18.8043 21.5232C18.133 22.4906 17.5834 23.1602 17.1598 23.532C16.5032 24.1424 15.7997 24.455 15.0464 24.4728C14.5056 24.4728 13.8534 24.3172 13.0942 24.0017C12.3325 23.6876 11.6325 23.532 10.9925 23.532C10.3212 23.532 9.60128 23.6876 8.83123 24.0017C8.06002 24.3172 7.43874 24.4816 6.96373 24.4979C6.24132 24.5291 5.52125 24.2076 4.80251 23.532C4.34376 23.1276 3.76997 22.4343 3.08259 21.4521C2.34508 20.4032 1.73875 19.187 1.26374 17.8004C0.75502 16.3026 0.5 14.8523 0.5 13.4482C0.5 11.8398 0.843837 10.4526 1.53254 9.29011C2.07379 8.35636 2.79386 7.61979 3.69508 7.07906C4.59629 6.53834 5.57006 6.26279 6.61872 6.24516C7.19251 6.24516 7.94497 6.42456 8.88004 6.77715C9.81247 7.13091 10.4112 7.31031 10.6737 7.31031C10.8699 7.31031 11.535 7.10054 12.6625 6.68233C13.7288 6.29449 14.6287 6.13391 15.3659 6.19716C17.3636 6.36012 18.8644 7.1561 19.8625 8.59013C18.0758 9.68432 17.1921 11.2169 17.2097 13.1829C17.2258 14.7142 17.7754 15.9886 18.8556 17.0004C19.3451 17.47 19.8918 17.833 20.5 18.0907C20.3681 18.4774 20.2289 18.8477 20.0808 19.2033ZM15.4993 0.980137C15.4993 2.18041 15.0654 3.3011 14.2007 4.3384C13.1572 5.57155 11.895 6.28412 10.5262 6.17168C10.5088 6.02769 10.4987 5.87614 10.4987 5.71688C10.4987 4.56462 10.9949 3.33147 11.8762 2.32321C12.3162 1.8127 12.8758 1.38823 13.5544 1.04962C14.2315 0.716055 14.872 0.531589 15.4744 0.5C15.4919 0.660458 15.4993 0.820926 15.4993 0.980121V0.980137Z"
        fill="#04081A"
      />
    </svg>
    <span class="mx-auto w-[150px] text-sw-dark font-medium">Continue with Apple</span>
  </button>
</template>
